import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaShieldAlt, 
  FaUserLock, 
  FaDatabase, 
  FaCookieBite, 
  FaShareAlt, 
  FaUserCheck, 
  FaEnvelope, 
  FaBuilding, 
  FaPhoneAlt,
  FaFileContract,
  FaUndoAlt,
  FaTimesCircle
} from 'react-icons/fa';
import NavbarComponent from './NavbarComponent';
import Footer from './Footer';
import './Policies.css';

const PrivacyPolicy = ({ isLoggedIn, username, userRole, handleLogout }) => {
  return (
    <div className="policy-page-container">
      <NavbarComponent 
        isLoggedIn={isLoggedIn} 
        username={username} 
        userRole={userRole} 
        handleLogout={handleLogout} 
      />

      {/* Hero Section */}
      <section className="policy-hero">
        <div className="policy-badge">
          <FaShieldAlt /> Legal & Compliance
        </div>
        <h1>Privacy Policy</h1>
        <p>
          At CodingBoss, we prioritize the protection and confidentiality of your personal, 
          academic, and technical data. Learn how we collect, handle, and secure your information.
        </p>
        <span className="policy-effective-date">Last Updated: September 2026</span>

        {/* Tab Navigation */}
        <div className="policy-nav-tabs">
          <Link to="/privacy-policy" className="policy-tab-link active">
            <FaShieldAlt /> Privacy Policy
          </Link>
          <Link to="/cancellation-policy" className="policy-tab-link">
            <FaTimesCircle /> Cancellation Policy
          </Link>
          <Link to="/refund-policy" className="policy-tab-link">
            <FaUndoAlt /> Refund Policy
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <main className="policy-main-content">
        <div className="policy-card">
          
          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaFileContract /> 1. Introduction
            </h2>
            <p className="policy-text">
              Welcome to <strong>CodingBoss</strong> ("we," "our," or "us"). We operate 
              <a href="https://codingboss.in" target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b', marginLeft: '5px' }}>
                codingboss.in
              </a> and associated educational platforms, tools, compiler environments, and assessment services.
            </p>
            <p className="policy-text">
              This Privacy Policy explains how your personal information is collected, processed, and safeguarded when you 
              register, enroll in courses, take proctored assessments, practice code in our compiler sandbox, or interact 
              with our platform.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaDatabase /> 2. Information We Collect
            </h2>
            <p className="policy-text">We collect information that you provide directly to us or through your institution:</p>
            <ul className="policy-list">
              <li>
                <strong>Identity & Account Data:</strong> Full name, institutional email address, student roll number/register number, college/department affiliation, and encrypted authentication credentials.
              </li>
              <li>
                <strong>Academic & Assessment Data:</strong> MCQ answers, coding question submissions, code execution metrics, test scores, contest rankings, activity streaks, and performance analytics.
              </li>
              <li>
                <strong>Proctoring & Verification Data:</strong> Camera snapshots, browser tab focus states, and session timestamps strictly captured during proctored college examinations to ensure academic integrity.
              </li>
              <li>
                <strong>Transaction & Billing Data:</strong> Payment transaction reference IDs, course enrollment records, and invoice details. Note: We do not store full credit/debit card numbers; transactions are securely handled by RBI-compliant payment gateways.
              </li>
              <li>
                <strong>Technical & Log Information:</strong> IP address, browser type, operating system, device telemetry, and platform usage logs.
              </li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaUserLock /> 3. How We Use Your Information
            </h2>
            <p className="policy-text">Your information is used strictly to provide and improve our educational services:</p>
            <ul className="policy-list">
              <li>To provide access to the student dashboard, learning paths, interactive IDE, and video courses.</li>
              <li>To evaluate code submissions, compute algorithmic contest rankings, and issue verifiable digital completion certificates.</li>
              <li>To deliver academic performance reports and audit logs to authorized college administrators, teachers, and trainers.</li>
              <li>To communicate important platform updates, password reset OTPs, contest schedules, and security alerts.</li>
              <li>To maintain anti-cheating protocols and enforce test completion blocking.</li>
            </ul>

            <div className="policy-highlight-box info">
              <h4>We Do Not Sell Your Data</h4>
              <p>
                CodingBoss will never sell, lease, or rent your personal information or student submission records to any 
                third-party advertisers or marketing agencies.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaShareAlt /> 4. Information Sharing & Disclosure
            </h2>
            <p className="policy-text">We only disclose your data under the following circumstances:</p>
            <ul className="policy-list">
              <li>
                <strong>Partner Institutions:</strong> If your account is affiliated with a college or university, authorized department staff and administrators have access to your test scores and assignment submissions.
              </li>
              <li>
                <strong>Service Providers:</strong> Trusted third-party cloud infrastructure (e.g., AWS, secure container clusters for remote code execution, and payment gateways) operating under strict non-disclosure agreements.
              </li>
              <li>
                <strong>Legal Requirements:</strong> If compelled by applicable Indian laws, judicial orders, or regulatory bodies.
              </li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaShieldAlt /> 5. Data Security & Cryptography
            </h2>
            <p className="policy-text">
              We implement enterprise-grade security protocols to protect your information:
            </p>
            <ul className="policy-list">
              <li>AES-256 cryptographic encryption for sensitive local identifiers and credential tokens.</li>
              <li>HTTPS/TLS encryption for all data in transit across our APIs.</li>
              <li>Isolated sandbox execution environments for running and testing student code safely.</li>
              <li>Role-based access control (RBAC) ensuring only authorized personnel access administrative data.</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaCookieBite /> 6. Cookies & Local Storage
            </h2>
            <p className="policy-text">
              We use session cookies and local storage tokens (`localStorage`) exclusively to keep you authenticated, 
              maintain your active IDE session, preserve test state during network blips, and store user preferences. You 
              can configure your browser to reject cookies, though certain dashboard features may not function properly.
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaUserCheck /> 7. Your Rights & Data Retention
            </h2>
            <p className="policy-text">
              You have the right to review, update, or correct your profile information via the Dashboard. You may also request 
              account deactivation through your institutional administrator or by writing to our compliance team. Assessment 
              records may be retained as mandated by partner college audit regulations.
            </p>
          </div>

          {/* Contact Details */}
          <div className="policy-contact-card">
            <h4>Grievance Officer & Privacy Inquiries</h4>
            <p className="policy-text" style={{ marginBottom: 0 }}>
              If you have any questions, concerns, or grievances regarding this Privacy Policy or your personal data, please contact:
            </p>
            <div className="policy-contact-grid">
              <a href="mailto:info@codingboss.com" className="contact-info-item">
                <FaEnvelope /> info@codingboss.com
              </a>
              <div className="contact-info-item">
                <FaBuilding /> CodingBoss EdTech Pvt. Ltd.
              </div>
              <a href="https://linkedin.com/company/codingboss" target="_blank" rel="noopener noreferrer" className="contact-info-item">
                <FaShareAlt /> linkedin.com/company/codingboss
              </a>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
